<template lang="pug">
SchedulesDropdown.views(
  label="View"
  icon="calendar"
  :options="viewItems"
  :initial-value="activeView"
  @filter-change="changeView"
)
</template>

<script>
import { mapInertiaProps } from '~/utils/inertia';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { Formats } from '~/utils/time_utils';
import SchedulesDropdown from '~/pages/Schedules/SchedulesDropdown.vue';

const ResizeObserver = window.ResizeObserver;

export default {
  name: 'SchedulesFiltersViewToggles',
  components: {
    SchedulesDropdown,
  },
  // Must be declared: without it the parent's `@filter-change` falls through (Vue 3 attrs) onto the
  // root <SchedulesDropdown>, whose own `filter-change` emit (raw `{key: paramName(undefined)}`
  // payload) then hits the parent handler directly — serialized as an empty `=month` query param,
  // so `active_view` never changed and the view snapped back.
  emits: ['filter-change'],
  data() {
    return {
      minWidth: 768,
      resizeObserver: null,
    };
  },
  computed: {
    ...mapInertiaProps({
      activeView: 'active_view',
      startWeekOn: ({ organization }) => organization.start_week_on,
      currentUserRole: 'current_user_role',
      organizationId: ({ organization }) => organization.id,
    }),
    viewItems() {
      return [
        { text: 'Month', id: 'month' },
        { text: 'Week', id: 'week' },
        { text: 'Day', id: 'day' },
      ];
    },
    isoWeekday() {
      return toMomentIsoWeekday(this.startWeekOn);
    },
  },
  mounted() {
    dayjs.extend(isoWeek);
    this.setView();
    this.setupResizeObserver();
  },
  beforeUnmount() {
    this.stopResizeObserver();
  },
  methods: {
    setupResizeObserver() {
      const resizeObserver = new ResizeObserver(() => {
        this.setView();
      });

      resizeObserver.observe(document.body);
    },
    stopResizeObserver() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
    },
    changeView(options) {
      const [{ value: viewName }] = options;
      const startDate = this.getStartDate(viewName);
      const stopDate = this.getStopDate(viewName);
      const filters = [
        { key: 'active_view', value: viewName },
        { key: 'start_date', value: this.iso8601(startDate) },
        { key: 'stop_date', value: this.iso8601(stopDate) },
      ];

      window.analytics?.track('Calendar table display by changed', {
        organization_id: this.organizationId,
        user_role: this.currentUserRole,
        current_page_title: document.title,
        current_url: document.url,
        preset_used: this.activeView,
        preset_value: viewName,
      });

      this.$emit('filter-change', filters, { preserveScroll: true });
    },
    iso8601(date) {
      return date.format(Formats.date.iso);
    },
    getStartDate(period) {
      if (period === 'week' || period === 'day') {
        return moment().startOfIsoWeek(this.isoWeekday);
      }

      return dayjs().startOf('month');
    },
    getStopDate(period) {
      if (period === 'week' || period === 'day') {
        return moment().endOfIsoWeek(this.isoWeekday);
      }

      return dayjs().endOf('month');
    },
    setView() {
      if (window.innerWidth <= this.minWidth && this.activeView !== 'day') {
        this.changeView([{ value: 'day' }]);
      }
    },
  },
};
</script>
